import React from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { IconButton } from "../../atoms/IconButton";
import { WorkspaceInitialsBadge } from "../../atoms/WorkspaceInitialsBadge";
import { SearchableFieldMenu } from "../SearchableFieldMenu";
import type { SearchableMenuOptionVm } from "../SearchableFieldMenu";
import {
  Chevron,
  HeaderRow,
  Label,
  Position,
  Root,
  Trigger,
  TriggerLabel,
  TriggerMeta,
} from "./PreviewAsPicker.styles";
import type { PreviewAsOption, PreviewAsPickerProps } from "./PreviewAsPicker.types";
import { usePreviewAsPickerInternalState } from "./usePreviewAsPickerInternalState";

const ICON_SIZE = 14;
const SEARCH_ICON_SIZE = 14;

const avatarOf = (option: PreviewAsOption) => option.avatar ?? <WorkspaceInitialsBadge name={option.label} size="xs" tone="categorical" decorative />;

export const PreviewAsPicker: React.FC<PreviewAsPickerProps> = ({
  options,
  value,
  onChange,
  copy,
  matches,
  disabled = false,
  className,
}) => {
  const state = usePreviewAsPickerInternalState({ options, value, onChange, matches });
  const { selected, index } = state;
  const noneToStep = disabled || options.length < 2;
  const menuOptions: SearchableMenuOptionVm[] = state.filtered.map((option) => ({
    id: option.id,
    label: option.label,
    hint: option.meta,
    icon: avatarOf(option),
  }));
  return (
    <Root className={className}>
      <HeaderRow>
        <Label>{copy.label}</Label>
        <IconButton icon={ChevronLeft} size="sm" iconSize={ICON_SIZE} aria-label={copy.previous} disabled={noneToStep} onClick={() => state.step(-1)} />
        {index === -1 ? null : <Position>{copy.position(index + 1, options.length)}</Position>}
        <IconButton icon={ChevronRight} size="sm" iconSize={ICON_SIZE} aria-label={copy.next} disabled={noneToStep} onClick={() => state.step(1)} />
      </HeaderRow>
      <Trigger
        ref={state.triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={state.anchor !== null}
        aria-label={copy.label}
        disabled={disabled}
        onClick={state.open}
      >
        {selected ? avatarOf(selected) : null}
        <TriggerLabel>{selected ? selected.label : copy.placeholder}</TriggerLabel>
        {selected?.meta ? <TriggerMeta>· {selected.meta}</TriggerMeta> : null}
        <Chevron aria-hidden="true">
          <ChevronDown size={16} />
        </Chevron>
      </Trigger>
      <SearchableFieldMenu
        anchor={state.anchor}
        groups={[{ id: "options", options: menuOptions }]}
        query={state.query}
        activeId={state.activeId}
        copy={{ label: copy.menuLabel, placeholder: copy.searchPlaceholder, empty: copy.empty }}
        searchIcon={<Search size={SEARCH_ICON_SIZE} aria-hidden="true" />}
        onQueryChange={state.setQuery}
        onActiveChange={state.setActiveId}
        onChoose={state.choose}
        onClose={state.close}
      />
    </Root>
  );
};

export default PreviewAsPicker;
